/**
 * Activity Monitor Service
 *
 * Tracks user activity (mouse, keyboard, touch, scroll) to determine
 * if the user is actively using the application. Used for intelligent
 * session refresh.
 */
import { getSessionConfig } from '../config/sessionConfig';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
export interface ActivityConfig {
    enabled: boolean;
    autoRefresh: boolean;
    idleThreshold: number; // milliseconds
    refreshCooldown: number; // min time between refreshes
    trackMouse: boolean;
    trackKeyboard: boolean;
    trackTouch: boolean;
    trackScroll: boolean;
}
type ActivityCallback = () => void;
class ActivityMonitorService {
    private static instance: ActivityMonitorService;
    private listeners: Set<ActivityCallback> = new Set();
    private lastActivityTime: number = Date.now();
    private isMonitoring: boolean = false;
    private config: ActivityConfig;
    // Debounce timers
    private mouseMoveTimer: NodeJS.Timeout | null = null;
    private scrollTimer: NodeJS.Timeout | null = null;
    private constructor() {
        // Initialize with default config, will be updated when startMonitoring is called
        this.config = {
            enabled: true,
            autoRefresh: false,
            idleThreshold: 30 * 60 * 1000, // 30 minutes default
            refreshCooldown: 5 * 60 * 1000, // 5 minutes default
            trackMouse: true,
            trackKeyboard: true,
            trackTouch: true,
            trackScroll: true
        };
    }
    public static getInstance(): ActivityMonitorService {
        if (!ActivityMonitorService.instance) {
            ActivityMonitorService.instance = new ActivityMonitorService();
        }
        return ActivityMonitorService.instance;
    }
    /**
     * Start monitoring user activity
     */
    public async startMonitoring(): Promise<void> {
        if (this.isMonitoring) {
            return;
        }

        // Load session config asynchronously
        try {
            const sessionConfig = await getSessionConfig();
            this.config = {
                enabled: true,
                autoRefresh: sessionConfig.autoRefreshEnabled,
                idleThreshold: sessionConfig.activityIdleTime,
                refreshCooldown: sessionConfig.refreshCooldown,
                trackMouse: true,
                trackKeyboard: true,
                trackTouch: true,
                trackScroll: true
            };
        } catch (error) {
            logger.warn('Failed to load session config for activity monitor, using defaults:', error);
        }

        logger.debug('👁️ Activity monitor started');
        this.isMonitoring = true;
        this.lastActivityTime = Date.now();
        if (this.config.trackMouse) {
            window.addEventListener('mousemove', this.handleMouseMove);
            window.addEventListener('click', this.handleActivity);
        }
        if (this.config.trackKeyboard) {
            window.addEventListener('keydown', this.handleActivity);
        }
        if (this.config.trackTouch) {
            window.addEventListener('touchstart', this.handleActivity);
            window.addEventListener('touchmove', this.handleActivity);
        }
        if (this.config.trackScroll) {
            window.addEventListener('scroll', this.handleScroll, true);
        }
        // Track visibility changes (tab active/inactive)
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('focus', this.handleActivity);
        window.addEventListener('blur', this.handleBlur);
    }
    /**
     * Stop monitoring user activity
     */
    public stopMonitoring(): void {
        if (!this.isMonitoring) {
            return;
        }
        logger.debug('👁️ Activity monitor stopped');
        this.isMonitoring = false;
        // Remove all event listeners
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('click', this.handleActivity);
        window.removeEventListener('keydown', this.handleActivity);
        window.removeEventListener('touchstart', this.handleActivity);
        window.removeEventListener('touchmove', this.handleActivity);
        window.removeEventListener('scroll', this.handleScroll, true);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('focus', this.handleActivity);
        window.removeEventListener('blur', this.handleBlur);
        // Clear timers
        if (this.mouseMoveTimer) {
            clearTimeout(this.mouseMoveTimer);
            this.mouseMoveTimer = null;
        }
        if (this.scrollTimer) {
            clearTimeout(this.scrollTimer);
            this.scrollTimer = null;
        }
    }
    /**
     * Register callback for activity events
     */
    public onActivity(callback: ActivityCallback): () => void {
        this.listeners.add(callback);
        // Return unsubscribe function
        return () => {
            this.listeners.delete(callback);
        };
    }
    /**
     * Get time since last activity (milliseconds)
     */
    public getTimeSinceLastActivity(): number {
        return Date.now() - this.lastActivityTime;
    }
    /**
     * Check if user is currently idle
     */
    public isIdle(): boolean {
        return this.getTimeSinceLastActivity() > this.config.idleThreshold;
    }
    /**
     * Get last activity timestamp
     */
    public getLastActivityTime(): number {
        return this.lastActivityTime;
    }
    /**
     * Update configuration
     */
    public updateConfig(newConfig: Partial<ActivityConfig>): void {
        this.config = { ...this.config, ...newConfig };
        // Restart monitoring if enabled state changed
        if (this.isMonitoring && !newConfig.enabled) {
            this.stopMonitoring();
        }
        else if (!this.isMonitoring && newConfig.enabled) {
            this.startMonitoring();
        }
    }
    /**
     * Handle generic activity event
     */
    private handleActivity = (): void => {
        this.registerActivity();
    };
    /**
     * Handle mouse move (debounced to prevent too many updates)
     */
    private handleMouseMove = (): void => {
        if (this.mouseMoveTimer) {
            return; // Debounced - ignore until timer expires
        }
        this.mouseMoveTimer = setTimeout(() => {
            this.mouseMoveTimer = null;
        }, 30000); // Debounce for 30 seconds
        this.registerActivity();
    };
    /**
     * Handle scroll (debounced to prevent too many updates)
     */
    private handleScroll = (): void => {
        if (this.scrollTimer) {
            return; // Debounced - ignore until timer expires
        }
        this.scrollTimer = setTimeout(() => {
            this.scrollTimer = null;
        }, 30000); // Debounce for 30 seconds
        this.registerActivity();
    };
    /**
     * Handle visibility change (tab becomes active/inactive)
     */
    private handleVisibilityChange = (): void => {
        if (!document.hidden) {
            // Tab became visible - register activity
            this.registerActivity();
        }
    };
    /**
     * Handle blur event (window loses focus)
     */
    private handleBlur = (): void => {
        // Don't update activity time when window loses focus
        // This prevents activity registration when user switches tabs
    };
    /**
     * Register activity and notify listeners
     */
    private registerActivity(): void {
        const now = Date.now();
        const timeSinceLastActivity = now - this.lastActivityTime;
        // Only register if enough time has passed (prevent spam)
        if (timeSinceLastActivity < 1000) {
            return;
        }
        this.lastActivityTime = now;
        // Notify all listeners
        this.listeners.forEach(callback => {
            try {
                callback();
            }
            catch (error) {
                logger.error('Error in activity callback:', error, {});
            }
        });
    }
    /**
     * Manually register activity (for programmatic use)
     */
    public manualActivity(): void {
        this.registerActivity();
    }
}
// Export singleton instance
export const activityMonitor = ActivityMonitorService.getInstance();
export default activityMonitor;
